/**
 * QA Panel — main controller (lobby side).
 *
 * Responsibilities:
 *   - Set up i18next post-processor + missing key tracker
 *   - Subscribe to appStore for language/asset changes
 *   - Manage popup window lifecycle (open/close/heartbeat)
 *   - Marshal state to the popup
 *   - Handle commands coming back from the popup
 *
 * The bridge is intentionally a simple class with explicit lifecycle —
 * no React, no Zustand store of its own. It plugs into the existing
 * lobby state without owning any of it.
 */

import packageJson from '../../../../package.json';
import i18next from '../../../../i18n';
import translationService from '../../../core/services/i18n/TranslationService';
import { useAppStore } from '../../../store/app/appStore';
import { useFeatureFlagsStore } from '../../../store/featureFlags/featureFlagsStore';
import type { BrandingType } from '../../../types/api/api.types';
import { MESSAGE_NAMESPACE, MESSAGE_TARGET_ORIGIN } from '../constants';
import { isQaPanelMessage } from '../messageProtocol';
import type { LobbyToPopupMessage, PopupToLobbyMessage } from '../messageProtocol';
import { getQaMode, registerQaPostProcessor, setQaMode } from '../i18n/qaPostProcessor';
import {
    computeCoverage,
    getLoadedLanguages,
    inspectLanguage,
} from '../utils/coverageCalculator';
import { JackpotMockController } from '../jackpot/jackpotMockController';
import { BackofficeController } from '../backoffice/backofficeController';
import { PresetController } from '../preset/presetController';
import { SentryDebugController } from '../sentry/sentryController';
import { CampaignsController } from '../campaigns/campaignsController';
import { PrizePopController } from '../campaigns/prizePopController';
import { RebateController } from '../campaigns/rebateController';
import { JiraController } from '../jira/jiraController';
import { ConsoleController } from '../console/consoleController';
import { EnvironmentController } from '../environment/environmentController';
import { JackpotWidgetController } from '../jackpotWidget/jackpotWidgetController';
import { MeasureController } from '../measure/measureController';
import * as measureOverlay from '../measure/measureOverlay';
// Figma tab disabled — re-enable by uncommenting
// import { FigmaController } from '../figma/figmaController';
// import * as figmaOverlay from '../figma/figmaOverlay';
import { eventTracker } from '../../../core/services/EventTracker';
import { transactionTracker } from '../../../core/services/TransactionTracker';
import { userDataTracker } from '../../../core/services/UserDataTracker';
import { sectionNavigationTracker } from '../../../core/services/SectionNavigationTracker';
import { dynamicSectionsTracker } from '../../../core/services/DynamicSectionsTracker';
import { sessionService } from '../../../core/services/SessionService';
import { buildMissingKeysCsv } from '../utils/exportMissingKeys';
import {
    clearMissingKeys,
    getMissingKeyRecords,
    installMissingKeyTracker,
    onMissingKey,
} from '../utils/missingKeyTracker';
import type { QaPanelSnapshot } from '../types';
import { buildPopupHtml } from './popupTemplate';
import { createFloatingButton } from './floatingButton';
import type { FloatingButton } from './floatingButton';
import { isCoarsePointer } from './container';
import type { PanelContainer } from './container';
import { PopupContainer } from './popupContainer';
import { OverlayContainer } from './overlayContainer';

export class QaPanelBridge {
    private container: PanelContainer | null = null;
    private heartbeatUnsubscribe: (() => void) | null = null;
    private storeUnsubscribe: (() => void) | null = null;
    private missingKeyUnsubscribe: (() => void) | null = null;
    private floatingButton: FloatingButton;
    private messageHandler: (event: MessageEvent) => void;
    private i18nLanguageHandler: () => void;
    private jackpotMock: JackpotMockController;
    private backoffice: BackofficeController;
    private preset: PresetController;
    private sentryDebug: SentryDebugController;
    private campaigns: CampaignsController;
    private prizePop: PrizePopController;
    private rebate: RebateController;
    private jira: JiraController;
    private consoleErrors: ConsoleController;
    private environment: EnvironmentController;
    private jackpotWidget: JackpotWidgetController;
    private measure: MeasureController;
    // Figma tab disabled — re-enable by uncommenting
    // private figma: FigmaController;

    constructor() {
        this.floatingButton = createFloatingButton(() => this.openPopup());
        this.messageHandler = this.handleMessage.bind(this);
        this.i18nLanguageHandler = () => this.sendState();
        this.jackpotMock = new JackpotMockController();
        this.backoffice = new BackofficeController();
        this.campaigns = new CampaignsController();
        this.prizePop = new PrizePopController();
        this.rebate = new RebateController();
        this.preset = new PresetController(
            this.backoffice,
            this.jackpotMock,
            this.campaigns,
            this.prizePop,
            this.rebate,
        );
        this.sentryDebug = new SentryDebugController();
        this.jira = new JiraController();
        this.consoleErrors = new ConsoleController();
        this.environment = new EnvironmentController();
        this.jackpotWidget = new JackpotWidgetController();
        this.measure = new MeasureController();
        // Figma tab disabled — re-enable by uncommenting
        // this.figma = new FigmaController();
    }

    /** Wire up listeners. Idempotent. Does NOT open the popup. */
    init(): void {
        registerQaPostProcessor();
        installMissingKeyTracker();

        // Forward incremental missing key observations.
        this.missingKeyUnsubscribe = onMissingKey((lang, key) => {
            this.sendMessage({
                namespace: MESSAGE_NAMESPACE,
                type: 'missingKey',
                payload: { lang, key },
            });
            // Also push a fresh full snapshot so coverage etc. stays in sync.
            this.sendState();
        });

        // Re-broadcast on language switch.
        i18next.on('languageChanged', this.i18nLanguageHandler);

        // Re-broadcast when supportedLanguages or asset config changes.
        this.storeUnsubscribe = useAppStore.subscribe((state, prevState) => {
            if (
                state.supportedLanguages !== prevState.supportedLanguages ||
                state.settings.languageCode !== prevState.settings.languageCode ||
                state.settings.assetsHost !== prevState.settings.assetsHost
            ) {
                this.sendState();
            }
        });

        // Listen for popup → lobby commands.
        window.addEventListener('message', this.messageHandler);

        // Wire jackpot mock controller — fires sendJackpotState whenever
        // its internal state changes (mock toggle, auto start/stop, store
        // updates, etc.) so the popup stays in sync.
        this.jackpotMock.init(() => {
            this.sendJackpotState();
            this.preset.onStateChange();
        });

        // Wire backoffice controller — re-broadcasts state on every store
        // change so the popup's section list stays live as edits happen.
        this.backoffice.init(() => {
            this.sendBackofficeState();
            this.preset.onStateChange();
        });

        // Wire preset controller — sends preset state to the popup whenever
        // auto-save status or stored preset info changes.
        this.preset.init(() => this.sendPresetState());

        // Wire sentry debug controller — polls every 2s and rebroadcasts the
        // tracker snapshot so the Sentry tab stays live.
        this.sentryDebug.init(() => this.sendSentryState());

        // Wire campaigns controller — re-broadcasts when mockMode toggles or
        // the tournament list changes so the popup's mock list stays live.
        // Also forwards into the preset's debounced auto-save so mocked
        // tournaments persist across reload when the toggle is on.
        this.campaigns.init(() => {
            this.sendCampaignsState();
            this.preset.onStateChange();
        });

        // Wire PrizePop controller — same pattern as campaigns, but targets
        // `campaignsStore.mockMode` / prize_pop slice. Preset auto-save is
        // forwarded so mocked PrizePop campaigns survive reload too.
        this.prizePop.init(() => {
            this.sendPrizePopState();
            this.preset.onStateChange();
        });

        // Wire Rebate controller — same pattern as PrizePop and campaigns, targets the
        // rebate slice of campaignsStore. Preset auto-save forwarded so
        // mocked Rebate campaigns survive reload too.
        this.rebate.init(() => {
            this.sendRebateState();
            this.preset.onStateChange();
        });

        // Wire Jira controller — build metadata is static, the callback will
        // never fire. Held only to match the controller-shape contract.
        this.jira.init(() => this.sendJiraState());

        // Wire Console controller — captures runtime errors / warnings and
        // re-broadcasts (debounced) so QA can inspect failures without
        // devtools open.
        this.consoleErrors.init(() => this.sendConsoleState());

        // Wire Environment controller — static casino list + on-demand
        // session-derived prefill. Callback exists only to match the
        // controller-shape contract; the controller never fires it.
        this.environment.init(() => this.sendEnvironmentState());

        // Wire the Jackpot Widget injector — loads the separate jackpot-widget
        // bundle from a QA-supplied URL and mounts it as a section on the lobby
        // page. Re-broadcasts on every form change and load-status transition.
        this.jackpotWidget.init(() => this.sendJackpotWidgetState());

        // Wire the Measure tool — ruler + guide lines drawn over the lobby.
        // Guides persist, but the overlay always starts off — it only appears
        // once switched on from the panel.
        this.measure.init(() => {
            this.reconcileMeasureOverlay();
            this.sendMeasureState();
        });

        // Figma tab disabled — re-enable by uncommenting
        // this.figma.init(
        //     () => {
        //         this.reconcileFigmaOverlay();
        //         this.sendFigmaState();
        //     },
        //     () => {
        //         this.reconcileFigmaOverlay();
        //         this.sendFigmaImage();
        //     },
        // );
    }

    /**
     * Open the panel. On desktop this is a window.open popup (must be invoked
     * from a user gesture, or the popup blocker intervenes — the floating
     * button is surfaced as a clickable fallback). On touch devices it's an
     * in-DOM overlay iframe, which needs no gesture.
     *
     * The container is chosen by a coarse-pointer check; everything downstream
     * (postMessage marshalling) is identical for either container.
     */
    openPopup(): void {
        if (this.container?.isOpen()) {
            this.container.focus();
            return;
        }

        const container: PanelContainer = isCoarsePointer()
            ? new OverlayContainer(() => this.closePopup())
            : new PopupContainer();

        const initialState = this.snapshot();
        const html = buildPopupHtml(JSON.stringify(initialState));

        if (!container.open(html)) {
            // Popup blocker (desktop) — show the floating button so the next
            // click works. The overlay never fails this way.
            this.floatingButton.show();
            return;
        }

        this.container = container;
        this.floatingButton.hide();

        // The popup can be closed by the OS window chrome; poll for that and
        // drop the reference. The overlay has no external-close path.
        this.heartbeatUnsubscribe =
            container.onExternalClose?.(() => {
                this.container = null;
                this.heartbeatUnsubscribe = null;
            }) ?? null;
    }

    closePopup(): void {
        this.heartbeatUnsubscribe?.();
        this.heartbeatUnsubscribe = null;
        this.container?.close();
        this.container = null;
    }

    togglePopup(): void {
        if (this.container?.isOpen()) {
            this.closePopup();
        } else {
            this.openPopup();
        }
    }

    destroy(): void {
        this.closePopup();
        this.floatingButton.destroy();
        this.storeUnsubscribe?.();
        this.missingKeyUnsubscribe?.();
        this.jackpotMock.destroy();
        this.backoffice.destroy();
        this.preset.destroy();
        this.sentryDebug.destroy();
        this.campaigns.destroy();
        this.prizePop.destroy();
        this.rebate.destroy();
        this.jira.destroy();
        this.consoleErrors.destroy();
        this.environment.destroy();
        this.jackpotWidget.destroy();
        measureOverlay.destroy();
        this.measure.destroy();
        // Figma tab disabled — re-enable by uncommenting
        // figmaOverlay.destroy();
        // this.figma.destroy();
        i18next.off('languageChanged', this.i18nLanguageHandler);
        window.removeEventListener('message', this.messageHandler);
    }

    /* ── Internal: state marshalling ─────────────────────────────── */

    private snapshot(): QaPanelSnapshot {
        const store = useAppStore.getState();
        return {
            currentLang: i18next.language || store.settings.languageCode || 'en',
            supportedLanguages: store.supportedLanguages,
            loadedLanguages: getLoadedLanguages(),
            mode: getQaMode(),
            missingKeys: getMissingKeyRecords(),
            coverage: computeCoverage(),
            referenceLang: 'en',
            appVersion: packageJson.version,
        };
    }

    private sendState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'state',
            payload: this.snapshot(),
        });
    }

    private sendJackpotState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'jackpot.state',
            payload: this.jackpotMock.getState(),
        });
    }

    private sendBackofficeState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'backoffice.state',
            payload: this.backoffice.getState(),
        });
    }

    private sendPresetState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'preset.state',
            payload: this.preset.getState(),
        });
    }

    private sendSentryState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'sentry.state',
            payload: this.sentryDebug.getState(),
        });
    }

    private sendCampaignsState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'campaigns.state',
            payload: this.campaigns.getState(),
        });
    }

    private sendPrizePopState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'prizepop.state',
            payload: this.prizePop.getState(),
        });
    }

    private sendRebateState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'rebate.state',
            payload: this.rebate.getState(),
        });
    }

    private sendJiraState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'jira.state',
            payload: this.jira.getState(),
        });
    }

    private sendConsoleState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'console.state',
            payload: this.consoleErrors.getState(),
        });
    }

    private sendEnvironmentState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'environment.state',
            payload: this.environment.getState(),
        });
    }

    private sendJackpotWidgetState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'jackpotWidget.state',
            payload: this.jackpotWidget.getState(),
        });
    }

    private sendMeasureState(): void {
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'measure.state',
            payload: this.measure.getState(),
        });
    }

    private reconcileMeasureOverlay(): void {
        const state = this.measure.getState();
        if (state.overlayOn) {
            measureOverlay.mount(state, this.measure);
        } else {
            measureOverlay.destroy();
        }
    }

    // Figma tab disabled — re-enable by uncommenting
    // private sendFigmaState(): void {
    //     this.sendMessage({
    //         namespace: MESSAGE_NAMESPACE,
    //         type: 'figma.state',
    //         payload: this.figma.getState(),
    //     });
    // }
    //
    // private sendFigmaImage(): void {
    //     this.sendMessage({
    //         namespace: MESSAGE_NAMESPACE,
    //         type: 'figma.image',
    //         payload: { image: this.figma.getImage() },
    //     });
    // }
    //
    // private reconcileFigmaOverlay(): void {
    //     const state = this.figma.getState();
    //     const image = this.figma.getImage();
    //     const natural = this.figma.getNaturalDims();
    //     if (state.overlayOn && state.hasImage && image !== null) {
    //         figmaOverlay.mount(state, image, natural, {
    //             onClose: () => this.figma.setOverlayOn(false),
    //             onOpacity: (opacity) => this.figma.setOpacity(opacity),
    //             onPositionCommit: (position) => this.figma.setPosition(position),
    //             onViewportResize: () => this.figma.reclampForViewport(),
    //         });
    //         figmaOverlay.update(state, image, natural);
    //     } else {
    //         figmaOverlay.destroy();
    //     }
    // }

    private sendMessage(message: LobbyToPopupMessage): void {
        const target = this.container?.target();
        if (!target) return;
        target.postMessage(message, MESSAGE_TARGET_ORIGIN);
    }

    /* ── Internal: incoming messages ─────────────────────────────── */

    private handleMessage(event: MessageEvent): void {
        if (!isQaPanelMessage<PopupToLobbyMessage>(event.data)) return;
        const msg = event.data;

        switch (msg.type) {
            case 'requestState':
                this.sendState();
                return;

            case 'switchLanguage': {
                // Update appStore settings.languageCode rather than calling
                // TranslationService.changeLanguage directly. The lobby has a
                // sync hook (useTranslations) that watches settings.languageCode
                // and re-derives i18n.language from it. Calling i18n directly
                // here causes the hook's useEffect to see a mismatch (store
                // still says EN, i18n says DE) and "correct" it back. Mirroring
                // the SettingsModal flow is the only safe path.
                useAppStore.getState().updateSettings({ languageCode: msg.payload.lang });
                // Push a state snapshot so the popup updates the highlighted
                // language button immediately, before useTranslations finishes
                // its async load.
                this.sendState();
                return;
            }

            case 'setMode':
                setQaMode(msg.payload.mode);
                this.sendState();
                return;

            case 'preloadAllLanguages':
                void this.preloadAllLanguages();
                return;

            case 'clearMissingKeys':
                clearMissingKeys();
                this.sendState();
                return;

            case 'exportMissingKeys': {
                const exp = buildMissingKeysCsv(getMissingKeyRecords(), computeCoverage());
                this.sendMessage({
                    namespace: MESSAGE_NAMESPACE,
                    type: 'exportReady',
                    payload: exp,
                });
                return;
            }

            case 'inspectLanguage': {
                const inspection = inspectLanguage(msg.payload.lang);
                this.sendMessage({
                    namespace: MESSAGE_NAMESPACE,
                    type: 'inspection',
                    payload: inspection,
                });
                return;
            }

            case 'jackpot.setMockMode':
                this.jackpotMock.setMockMode(msg.payload.enabled);
                return;

            case 'jackpot.startAuto':
                this.jackpotMock.startAuto(msg.payload.intervalSec, msg.payload.percent);
                return;

            case 'jackpot.stopAuto':
                this.jackpotMock.stopAuto();
                return;

            case 'jackpot.requestState':
                this.sendJackpotState();
                return;

            case 'jackpot.setLevelValue':
                this.jackpotMock.setLevelValue(
                    msg.payload.jackpotName,
                    msg.payload.levelId,
                    msg.payload.value
                );
                return;

            case 'jackpot.injectInstant':
                this.jackpotMock.injectInstantJackpot(msg.payload.value);
                return;

            case 'jackpot.setWinsMockMode':
                this.jackpotMock.setWinsMockMode(msg.payload.enabled);
                return;

            case 'jackpot.generateWinStats': {
                const rows = this.jackpotMock.generateWinStats({
                    jackpotName: msg.payload.jackpotName,
                    winsPerLevel: msg.payload.winsPerLevel,
                    includeZeroWinLevel: msg.payload.includeZeroWinLevel,
                });
                // Echo the outcome so the popup reports failure (-1: games not loaded) honestly
                // instead of claiming success for a generate that produced nothing.
                this.sendMessage({
                    namespace: MESSAGE_NAMESPACE,
                    type: 'jackpot.winStatsGenerated',
                    payload: { jackpotName: msg.payload.jackpotName, rows },
                });
                return;
            }

            case 'jackpot.clearWinStats':
                this.jackpotMock.clearWinStats(msg.payload.jackpotName);
                return;

            case 'backoffice.requestState':
                this.sendBackofficeState();
                return;

            case 'backoffice.renameSection':
                this.backoffice.renameSection(msg.payload.oldName, msg.payload.newName);
                return;

            case 'backoffice.deleteSection':
                this.backoffice.deleteSection(msg.payload.name);
                return;

            case 'backoffice.addGame':
                this.backoffice.addGameToSection(msg.payload.sectionName, msg.payload.gameKey);
                return;

            case 'backoffice.removeGame':
                this.backoffice.removeGameFromSection(msg.payload.sectionName, msg.payload.gameKey);
                return;

            case 'backoffice.setSectionOrder':
                this.backoffice.setSectionOrder(msg.payload.names);
                return;

            case 'backoffice.setGameOrder':
                this.backoffice.setGameOrder(msg.payload.sectionName, msg.payload.gameKeys);
                return;

            case 'backoffice.updateGame':
                this.backoffice.updateGame(msg.payload.gameKey, msg.payload.patch);
                return;

            case 'backoffice.resetToApi':
                void this.backoffice.resetToApi();
                return;

            case 'backoffice.setOpenType':
                useAppStore.getState().updateSettings({ openType: msg.payload.openType });
                this.sendBackofficeState();
                return;

            case 'backoffice.setSeasonalBranding': {
                const val = msg.payload.value;
                if (val) {
                    useFeatureFlagsStore.getState().setSeasonalBranding(val as BrandingType);
                } else {
                    useFeatureFlagsStore.getState().setFlags({
                        ...useFeatureFlagsStore.getState().flags,
                        seasonalBranding: null,
                    });
                }
                this.sendBackofficeState();
                return;
            }

            case 'backoffice.setNoJackpot': {
                const current = useAppStore.getState().settings.restrictions ?? [];
                const filtered = current.filter((r) => r !== 'noJackpot');
                const next = msg.payload.noJackpot ? [...filtered, 'noJackpot'] : filtered;
                useAppStore.getState().updateSettings({ restrictions: next });
                this.sendBackofficeState();
                return;
            }

            case 'backoffice.setCurrency':
                this.backoffice.setCurrency(msg.payload.currency);
                this.sendBackofficeState();
                return;

            case 'backoffice.setHotColdMock':
                this.backoffice.setHotColdMock(msg.payload.enabled);
                this.sendBackofficeState();
                return;

            case 'backoffice.setCampaignFlag':
                this.backoffice.setCampaignFlag(msg.payload.flag, msg.payload.value);
                this.sendBackofficeState();
                return;

            case 'backoffice.checkThumbnails':
                void this.checkThumbnails();
                return;

            case 'preset.requestState':
                this.sendPresetState();
                return;

            case 'preset.requestExport': {
                const preset = this.preset.buildPreset();
                const json = JSON.stringify(preset, null, 2);
                const date = new Date().toISOString().slice(0, 10);
                this.sendMessage({
                    namespace: MESSAGE_NAMESPACE,
                    type: 'preset.exportReady',
                    payload: { json, filename: `qa-preset-${date}.json` },
                });
                return;
            }

            case 'preset.import': {
                const result = this.preset.applyPreset(msg.payload.preset);
                this.sendMessage({
                    namespace: MESSAGE_NAMESPACE,
                    type: 'preset.importResult',
                    payload: result,
                });
                // Force full state broadcast so the popup refreshes all tabs.
                // Send immediately AND after a short delay — the immediate
                // broadcast covers most cases, but some store subscribers
                // (jackpot mock, tournament cup) may process async and the
                // popup needs the final settled state.
                this.sendState();
                this.sendBackofficeState();
                this.sendJackpotState();
                this.sendPresetState();
                this.sendCampaignsState();
                this.sendPrizePopState();
                setTimeout(() => {
                    this.sendState();
                    this.sendBackofficeState();
                    this.sendJackpotState();
                    this.sendPresetState();
                    this.sendCampaignsState();
                    this.sendPrizePopState();
                }, 200);
                return;
            }

            case 'preset.setAutoSave':
                this.preset.setAutoSave(msg.payload.enabled);
                this.sendPresetState();
                return;

            case 'preset.clearStorage':
                this.preset.clearStorage();
                this.sendPresetState();
                return;

            case 'sentry.clearAll':
                // Wipe each tracker singleton independently — same defensive
                // posture as the controller reads. One bad call must not skip
                // the rest of the clears.
                try { eventTracker.clear(); } catch { /* ignore */ }
                try { transactionTracker.clear(); } catch { /* ignore */ }
                try { userDataTracker.clear(); } catch { /* ignore */ }
                try { sectionNavigationTracker.clear(); } catch { /* ignore */ }
                try { dynamicSectionsTracker.clear(); } catch { /* ignore */ }
                try { sessionService.clearSearchQueries(); } catch { /* ignore */ }
                this.sendSentryState();
                return;

            case 'sentry.syncToSentry':
                // updateEventData() is the public entry point — internally it
                // attaches event, transaction, user, and section-navigation
                // data onto the active span in one pass.
                try { sessionService.updateEventData(); } catch { /* ignore */ }
                this.sendSentryState();
                return;

            case 'campaigns.requestState':
                this.sendCampaignsState();
                return;

            case 'campaigns.createMockTournament': {
                const id = this.campaigns.createMockTournament(msg.payload.form);
                this.sendMessage({
                    namespace: MESSAGE_NAMESPACE,
                    type: 'campaigns.created',
                    payload: { id, name: msg.payload.form.name },
                });
                this.sendCampaignsState();
                return;
            }

            case 'campaigns.removeMockTournament':
                this.campaigns.removeMockTournament(msg.payload.id);
                this.sendCampaignsState();
                return;

            case 'campaigns.updateTournament':
                this.campaigns.updateTournamentField(msg.payload.id, msg.payload.patch);
                this.sendCampaignsState();
                return;

            case 'campaigns.enableMocking':
                this.campaigns.enableMocking();
                this.sendCampaignsState();
                return;

            case 'campaigns.disableMocking':
                this.campaigns.disableMocking();
                this.sendCampaignsState();
                return;

            case 'campaigns.fetchExternal':
                void this.fetchExternalCampaigns(msg.payload.sessionKey, msg.payload.playerId);
                return;

            case 'campaigns.clearAll':
                this.campaigns.clearAll();
                this.sendCampaignsState();
                return;

            case 'prizepop.requestState':
                this.sendPrizePopState();
                return;

            case 'prizepop.createMockCampaign': {
                const id = this.prizePop.createMockCampaign(msg.payload.form);
                this.sendMessage({
                    namespace: MESSAGE_NAMESPACE,
                    type: 'prizepop.created',
                    payload: { id, name: msg.payload.form.name },
                });
                this.sendPrizePopState();
                return;
            }

            case 'prizepop.removeMockCampaign':
                this.prizePop.removeMockCampaign(msg.payload.id);
                this.sendPrizePopState();
                return;

            case 'prizepop.updateCampaign':
                this.prizePop.updateCampaignField(msg.payload.id, msg.payload.patch);
                this.sendPrizePopState();
                return;

            case 'prizepop.enableMocking':
                this.prizePop.enableMocking();
                this.sendPrizePopState();
                return;

            case 'prizepop.disableMocking':
                this.prizePop.disableMocking();
                this.sendPrizePopState();
                return;

            case 'prizepop.fetchExternal':
                void this.fetchExternalPrizePop(msg.payload.sessionKey, msg.payload.playerId);
                return;

            case 'prizepop.clearAll':
                this.prizePop.clearAll();
                this.sendPrizePopState();
                return;

            case 'rebate.requestState':
                this.sendRebateState();
                return;

            case 'rebate.createMockCampaign': {
                const id = this.rebate.createMockCampaign(msg.payload.form);
                this.sendMessage({
                    namespace: MESSAGE_NAMESPACE,
                    type: 'rebate.created',
                    payload: { id, name: msg.payload.form.name },
                });
                this.sendRebateState();
                return;
            }

            case 'rebate.removeMockCampaign':
                this.rebate.removeMockCampaign(msg.payload.id);
                this.sendRebateState();
                return;

            case 'rebate.updateCampaign':
                this.rebate.updateCampaignField(msg.payload.id, msg.payload.patch);
                this.sendRebateState();
                return;

            case 'rebate.enableMocking':
                this.rebate.enableMocking();
                this.sendRebateState();
                return;

            case 'rebate.disableMocking':
                this.rebate.disableMocking();
                this.sendRebateState();
                return;

            case 'rebate.fetchExternal':
                void this.fetchExternalRebate(msg.payload.sessionKey, msg.payload.playerId);
                return;

            case 'rebate.clearAll':
                this.rebate.clearAll();
                this.sendRebateState();
                return;

            case 'jira.requestState':
                this.sendJiraState();
                return;

            case 'console.requestState':
                this.sendConsoleState();
                return;

            case 'console.clear':
                this.consoleErrors.clear();
                this.sendConsoleState();
                return;

            case 'environment.requestState':
                this.sendEnvironmentState();
                return;

            case 'jackpotWidget.requestState':
                this.sendJackpotWidgetState();
                return;

            case 'jackpotWidget.setConfig':
                this.jackpotWidget.setConfig(msg.payload.patch);
                return;

            case 'jackpotWidget.loadZip':
                // Fire-and-forget: loadZip() reports progress and any failure
                // through its own state broadcast.
                void this.jackpotWidget.loadZip(msg.payload.name, msg.payload.bytes);
                return;

            case 'jackpotWidget.apply':
                // Fire-and-forget: apply() reports both success and failure
                // through its own state broadcast, so there is nothing to await
                // and nothing useful to do with a rejection here.
                void this.jackpotWidget.apply();
                return;

            case 'jackpotWidget.disable':
                this.jackpotWidget.disable();
                return;

            case 'measure.requestState':
                this.sendMeasureState();
                return;

            case 'measure.set':
                this.measure.set(msg.payload.change);
                return;

            case 'measure.startPick':
                // Mounts the overlay synchronously (via the state callback)
                // before arming pick mode.
                this.measure.set({ overlayOn: true });
                measureOverlay.startPick();
                // On a phone the panel is a full-screen iframe over the
                // lobby — get it out of the way so the tap can land.
                if (isCoarsePointer()) this.closePopup();
                return;

            case 'measure.setRuler':
                this.measure.setRuler(msg.payload.patch);
                return;

            case 'measure.resetRuler':
                this.measure.resetRuler();
                return;

            case 'measure.addGuide':
                this.measure.addGuide(msg.payload.axis);
                return;

            case 'measure.setGuide':
                this.measure.setGuide(msg.payload.id, msg.payload.pos);
                return;

            case 'measure.removeGuide':
                this.measure.removeGuide(msg.payload.id);
                return;

            case 'measure.clearGuides':
                this.measure.clearGuides();
                return;

            case 'measure.clearSaved':
                this.measure.clearSaved();
                return;

            // Figma tab disabled — re-enable by uncommenting
            // case 'figma.requestState':
            //     this.sendFigmaState();
            //     return;
            //
            // case 'figma.requestImage':
            //     this.sendFigmaImage();
            //     return;
            //
            // case 'figma.setImage':
            //     this.figma.setImage(
            //         msg.payload.image,
            //         msg.payload.naturalWidth,
            //         msg.payload.naturalHeight,
            //     );
            //     return;
            //
            // case 'figma.setOverlayOn':
            //     this.figma.setOverlayOn(msg.payload.on);
            //     return;
            //
            // case 'figma.setOpacity':
            //     this.figma.setOpacity(msg.payload.opacity);
            //     return;
            //
            // case 'figma.setPosition':
            //     this.figma.setPosition({ x: msg.payload.x, y: msg.payload.y });
            //     return;
            //
            // case 'figma.setScale':
            //     this.figma.setScale(msg.payload.scale);
            //     return;
            //
            // case 'figma.resetView':
            //     this.figma.setPosition({ x: 0, y: 0 });
            //     this.figma.resetScaleToDefault();
            //     return;

            default: {
                // Exhaustiveness check — TypeScript will flag any missing case.
                const _exhaustive: never = msg;
                void _exhaustive;
            }
        }
    }

    /**
     * Force-load every supportedLanguage into i18next so the coverage matrix
     * is complete. Called when QA hits the "Preload all" button.
     */
    private async preloadAllLanguages(): Promise<void> {
        const { supportedLanguages, settings } = useAppStore.getState();
        const { assetsHost, assetsHash } = settings;
        if (!assetsHost) return;

        const tasks = supportedLanguages.map((lang) =>
            translationService.loadLanguage(lang, assetsHost, assetsHash).catch(() => undefined)
        );
        await Promise.all(tasks);
        this.sendState();
    }

    /**
     * One-shot external tournaments fetch driven by QA-pasted creds. Always
     * sends a `campaigns.fetchResult` back, plus a fresh state broadcast on
     * success so the popup's editable list repopulates.
     */
    private async fetchExternalCampaigns(sessionKey: string, playerId: string): Promise<void> {
        const result = await this.campaigns.fetchExternal(sessionKey, playerId);
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'campaigns.fetchResult',
            payload: result,
        });
        if (result.ok) {
            this.sendCampaignsState();
        }
    }

    /**
     * One-shot external PrizePop fetch driven by QA-pasted creds. Always
     * sends a `prizepop.fetchResult` back, plus a fresh state broadcast on
     * success so the popup's editable list repopulates.
     */
    private async fetchExternalPrizePop(sessionKey: string, playerId: string): Promise<void> {
        const result = await this.prizePop.fetchExternal(sessionKey, playerId);
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'prizepop.fetchResult',
            payload: result,
        });
        if (result.ok) {
            this.sendPrizePopState();
        }
    }

    /**
     * One-shot external Rebate fetch driven by QA-pasted creds. Always
     * sends a `rebate.fetchResult` back, plus a fresh state broadcast on
     * success so the popup's editable list repopulates.
     */
    private async fetchExternalRebate(sessionKey: string, playerId: string): Promise<void> {
        const result = await this.rebate.fetchExternal(sessionKey, playerId);
        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'rebate.fetchResult',
            payload: result,
        });
        if (result.ok) {
            this.sendRebateState();
        }
    }

    /**
     * Check every game thumbnail for CDN availability via HEAD requests.
     * Sends results back to the popup when done.
     */
    private async checkThumbnails(): Promise<void> {
        const store = useAppStore.getState();
        const { assetsHost, assetsHash } = store.settings;
        if (!assetsHost) return;

        const rootPath = assetsHash && assetsHash.trim() !== ''
            ? `/${assetsHash}/assets/portal`
            : '/assets/portal';
        const hashSuffix = assetsHash && assetsHash.trim() !== ''
            ? `?hash=${assetsHash}`
            : '';

        const rawGames = store.supportedGames as Record<string, { name?: string }>;
        const gameKeys = Object.keys(rawGames);

        type MissingEntry = { gameKey: string; name: string; url: string };
        const missing: MissingEntry[] = [];

        // Check in batches of 20 to avoid overwhelming the browser with
        // hundreds of simultaneous connections.
        const BATCH_SIZE = 20;
        for (let i = 0; i < gameKeys.length; i += BATCH_SIZE) {
            const batch = gameKeys.slice(i, i + BATCH_SIZE);
            const results = await Promise.all(
                batch.map(async (gameKey) => {
                    const url = `${assetsHost}${rootPath}/verticals/${gameKey}.webp${hashSuffix}`;
                    try {
                        const res = await fetch(url, { method: 'HEAD' });
                        if (!res.ok) {
                            return { gameKey, name: rawGames[gameKey]?.name ?? gameKey, url };
                        }
                        return null;
                    } catch {
                        return { gameKey, name: rawGames[gameKey]?.name ?? gameKey, url };
                    }
                })
            );
            for (const r of results) {
                if (r) missing.push(r);
            }
        }

        this.sendMessage({
            namespace: MESSAGE_NAMESPACE,
            type: 'backoffice.thumbnailResults',
            payload: { total: gameKeys.length, missing },
        });
    }
}
